// Sweetspot glue: no CloudBTL session, Proposal API, tenant-admin override, or bearer storage.
export interface Material {
  kind: 'context' | 'file';
  title: string;
  description: string;
  source_kind: 'local' | 'slack' | 'notion' | 'drive' | 'user_context' | 'other';
  source_locator: string;
  representation: 'original' | 'excerpt' | 'user_statement' | 'assistant_summary';
  source_visibility: 'company' | 'limited' | 'only_me' | 'unknown';
  sharing_check: 'business_non_sensitive' | 'uncertain' | 'excluded';
  content_text: string;
  file_name: string;
  file_size: number;
  file_sha256: string;
}
export interface InboxSummary {
  item_id: string;
  version: number;
  kind: Material['kind'];
  state: 'awaiting_file' | 'quarantined' | 'deleted';
  title: string;
  description: string;
  expires_at: number;
}
export interface InboxItem extends InboxSummary, Material {
  submitted_by: { email: string; name: string; org_code: string };
  created_at: number;
  updated_at: number;
  history: { operation: string; version: number; created_at: number }[];
}
export interface InboxSession {
  user: { email: string; name: string; org_code: string };
  expires_at: number;
}
export interface InboxClient {
  loginUrl: string;
  session(): Promise<InboxSession>;
  list(before?: string): Promise<{ items: InboxSummary[]; next_before_id: string | null }>;
  get(id: string): Promise<InboxItem>;
  update(item: InboxItem, material: Material): Promise<InboxItem>;
  remove(item: InboxItem): Promise<void>;
  download(item: InboxItem): Promise<Blob>;
  upload(item: InboxItem, file: File): Promise<void>;
  logout(): Promise<void>;
}

export class InboxApiError extends Error {
  constructor(
    public status: number,
    public code: string,
  ) {
    super(code);
  }
}

export function inboxAvailable(host = window.location.hostname): boolean {
  return (
    host === 'document.sweetsophy.com' ||
    (import.meta.env.DEV && ['localhost', '127.0.0.1'].includes(host))
  );
}

export function inboxOrigin(): string {
  if (import.meta.env.DEV && ['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    const candidate = import.meta.env.VITE_COMPANY_BRAIN_INBOX_API;
    if (candidate) {
      const u = new URL(candidate);
      if (
        u.protocol !== 'http:' ||
        u.hostname !== window.location.hostname ||
        u.pathname !== '/' ||
        u.search ||
        u.hash ||
        u.username ||
        u.password
      ) {
        throw new Error('Invalid local inbox API origin');
      }
      return u.origin;
    }
  }
  return 'https://mcp.sweetsophy.com';
}

// Explicit allowlist: submitted_by, state, timestamps and review claims never round-trip as input.
export function materialOf(item: InboxItem): Material {
  return {
    kind: item.kind,
    title: item.title,
    description: item.description,
    source_kind: item.source_kind,
    source_locator: item.source_locator,
    representation: item.representation,
    source_visibility: item.source_visibility,
    sharing_check: item.sharing_check,
    content_text: item.content_text || '',
    file_name: item.file_name || '',
    file_size: item.file_size || 0,
    file_sha256: item.file_sha256 || '',
  };
}

export function sourceLink(value: string): string | null {
  try {
    const u = new URL(value);
    return ['https:', 'http:'].includes(u.protocol) && !u.username && !u.password ? u.href : null;
  } catch {
    return null;
  }
}

export async function digest(blob: Blob): Promise<string> {
  return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', await blob.arrayBuffer())))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function createInboxClient(origin = inboxOrigin()): InboxClient {
  const base = origin + '/inbox/web';
  let csrf = '';
  async function request(path: string, init: RequestInit = {}): Promise<Response> {
    const headers = new Headers(init.headers);
    if (csrf) headers.set('X-Inbox-CSRF', csrf);
    let r: Response;
    try {
      r = await fetch(base + path, {
        ...init,
        headers,
        credentials: 'include',
        cache: 'no-store',
        redirect: 'error',
        signal: AbortSignal.timeout(35000),
      });
    } catch {
      throw new InboxApiError(0, 'network_unavailable');
    }
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      if (r.status === 401) csrf = '';
      throw new InboxApiError(r.status, body.error || 'inbox_unavailable');
    }
    return r;
  }
  async function command<T>(value: unknown): Promise<T> {
    return (
      await request('/api', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(value),
      })
    ).json();
  }
  return {
    loginUrl: base + '/auth/start',
    async session() {
      const value = await (await request('/session')).json();
      csrf = value.csrf;
      return { user: value.user, expires_at: value.expires_at };
    },
    list: (before = '') => command({ action: 'list', before_id: before }),
    get: (id) => command({ action: 'get', item_id: id }),
    update: (item, material) =>
      command({
        action: 'update',
        item_id: item.item_id,
        expected_version: item.version,
        material,
      }),
    async remove(item) {
      await command({ action: 'delete', item_id: item.item_id, expected_version: item.version });
    },
    async download(item) {
      const blob = await (
        await request('/files/' + encodeURIComponent(item.item_id), {
          method: 'POST',
          headers: { 'X-Inbox-Version': String(item.version) },
        })
      ).blob();
      if (blob.size !== item.file_size || (await digest(blob)) !== item.file_sha256)
        throw new InboxApiError(400, 'file_hash_mismatch');
      return blob;
    },
    async upload(item, file) {
      if (file.size !== item.file_size || (await digest(file)) !== item.file_sha256)
        throw new InboxApiError(400, 'file_hash_mismatch');
      await request('/files/' + encodeURIComponent(item.item_id), {
        method: 'PUT',
        headers: {
          'X-Inbox-Version': String(item.version),
          'Content-Type': 'application/octet-stream',
        },
        body: file,
      });
    },
    async logout() {
      await request('/logout', { method: 'POST' });
      csrf = '';
    },
  };
}
